import React, { useState } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { NavLink, useNavigate } from "react-router-dom";
import { UPLOAD_URL } from "../../../utils/constants";
import { useParams, useLocation } from "react-router-dom";

type NavbarProps = {
  navbarLogo?: string;
  bureauName?: string;
};

const Navbar: React.FC<NavbarProps> = ({ navbarLogo, bureauName }) => {
  const navigate = useNavigate();
  const { id, bureauId } = useParams();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const profileBureauId = id || bureauId || "";
  const homePath = `/${id ? id : bureauId}/${bureauName ? bureauName.toLowerCase().replace(/\s+/g, "") : "home"}`;

  const goHome = () => {
    if (location.pathname === homePath) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      navigate(homePath);
    }
  };

  const goMaleProfiles = () => navigate(`${homePath}/profiles/male`);

  const goFemaleProfiles = () => navigate(`${homePath}/profiles/female`);

  const goSuccessStories = () => {
    navigate(`${homePath}/success-stories`);
  };

  const goToPackages = () => {
    if (location.pathname === homePath) {
      document
        .getElementById("packages-section")
        ?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(homePath, {
        state: { scrollTo: "packages-section" },
      });
    }
  };

  const navItems = [
    { label: "Home", action: goHome },
    { label: "Male Profiles", action: goMaleProfiles },
    { label: "Female Profiles", action: goFemaleProfiles },
    { label: "Success Stories", action: goSuccessStories },
    { label: "Packages", action: goToPackages },
  ];

  const redirectToPlans = () => {
    navigate("/admin/membership/plans", { state: { fromShare: true } });
  };

  const openDownloadApp = () => {
    alert("📲 Mobile App Coming Soon!");
  };

  React.useEffect(() => {
    document.body.style.overflow = isMobileOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobileOpen]);

  return (
    <div className="fixed top-0 left-0 z-50 w-full overflow-x-hidden safe-area-top">
      <div className="bg-rose-900 text-rose-100 text-xs sm:text-sm">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center gap-1 px-3 py-2 text-center md:flex-row md:items-center md:justify-between md:gap-2 md:px-8 md:text-left">
          <span className="flex min-w-0 items-center justify-center gap-1 sm:gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            <span className="break-words">
              Available across major cities in India
            </span>
          </span>
          <span className="hidden text-rose-300 sm:hidden">|</span>
          <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2">
            <a
              href="https://meemarriage.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white whitespace-nowrap"
            >
              Sign In
            </a>
            <span className="text-rose-300">|</span>
            <NavLink
              to={`/profile/${profileBureauId}`}
              className="hover:text-white whitespace-nowrap"
            >
              Free Register
            </NavLink>
          </div>
        </div>
      </div>

      <nav className="border-b border-slate-200/60 bg-white/90 px-4 py-2 shadow-sm backdrop-blur sm:px-6 sm:py-4 md:px-12 lg:px-32">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Logo */}
          <div className="flex flex-shrink-0 items-center min-w-0 sm:ml-4">
            {navbarLogo && (
              <img
                src={`${UPLOAD_URL}/bureaus/navbar_logos/${navbarLogo}`}
                alt="Bureau Logo"
                className="h-14 w-auto object-contain"
              />
            )}
          </div>

          <div className="hidden md:flex flex-1 items-center justify-end gap-6">
            <div className="flex flex-wrap items-center gap-2 xl:gap-3">
              {navItems.map((item, i) => (
                <button
                  key={i}
                  onClick={item.action}
                  className="rounded-full px-3 py-2 text-sm font-semibold text-slate-700 transition hover:text-rose-700 whitespace-nowrap"
                >
                  <span>{item.label}</span>
                </button>
              ))}
            </div>

            <button
              onClick={openDownloadApp}
              className="flex items-center gap-2 rounded-full border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700 transition hover:border-rose-400 hover:bg-rose-50 whitespace-nowrap"
            >
              Download App
            </button>

            <NavLink
              to={`/profile/${profileBureauId}`}
              className="flex items-center gap-1 rounded-full px-6 py-2.5 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 transition shadow-sm whitespace-nowrap"
            >
              Register Free
            </NavLink>
          </div>

          {/* Mobile Menu Icon */}
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            aria-label="Toggle Menu"
            className="md:hidden text-gray-700 focus:outline-none"
          >
            {isMobileOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
          </button>
        </div>
      </nav>

      {isMobileOpen && (
        <div className="fixed inset-0 z-[99999] md:hidden">
          <button
            aria-label="Close menu"
            onClick={() => setIsMobileOpen(false)}
            className="absolute inset-0 bg-black/50"
          />

          <div className="absolute right-0 top-0 h-screen w-[86%] max-w-[340px] overflow-y-auto bg-white shadow-2xl">
            <div className="border-b border-rose-100 bg-gradient-to-b from-rose-50 to-white px-5 pb-4 pt-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  {navbarLogo ? (
                    <img
                      src={`${UPLOAD_URL}/bureaus/navbar_logos/${navbarLogo}`}
                      alt="Logo"
                      className="h-10 w-10 rounded-xl border border-rose-100 bg-white object-contain"
                    />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100 font-bold text-rose-700">
                      RM
                    </div>
                  )}

                  <div className="min-w-0">
                    <div className="truncate font-bold text-rose-900">
                      {bureauName || ""}
                    </div>
                    <div className="text-xs text-rose-500">
                      Trusted matchmaking
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setIsMobileOpen(false)}
                  className="rounded-full p-2 text-slate-600 hover:bg-rose-50"
                  aria-label="Close"
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => {
                    openDownloadApp();
                    setIsMobileOpen(false);
                  }}
                  className="flex-1 rounded-xl border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-50"
                >
                  Download App
                </button>

                <NavLink
                  to={`/profile/${profileBureauId}`}
                  onClick={() => setIsMobileOpen(false)}
                  className="flex-1 rounded-xl bg-rose-600 px-3 py-2 text-center text-sm font-semibold text-white transition hover:bg-rose-700"
                >
                  Free Register
                </NavLink>
              </div>
            </div>

            <div className="px-3 py-4">
              <div className="mb-2 px-2 text-[11px] uppercase tracking-widest text-slate-400">
                Browse
              </div>

              <div className="space-y-1">
                {navItems.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      item.action();
                      setIsMobileOpen(false);
                    }}
                    className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-left font-semibold text-slate-700 transition hover:bg-rose-50 hover:text-rose-700"
                  >
                    <span>{item.label}</span>
                    <span className="text-slate-300">›</span>
                  </button>
                ))}
              </div>

              <div className="my-5 h-px bg-rose-100" />

              <div className="mb-2 px-2 text-[11px] uppercase tracking-widest text-slate-400">
                Account
              </div>

              <div className="space-y-1">
                <a
                  href="https://meemarriage.com/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileOpen(false)}
                  className="block rounded-xl px-4 py-3 font-semibold text-slate-700 transition hover:bg-rose-50 hover:text-rose-700"
                >
                  User Login
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
